import instance, { ADMIN_PREFIX } from 'services/api';
import {
  OrganizationDetail,
  OrganizationInput,
  OrganizationListRow
} from 'types/api/organizations';

/**
 * GET /api/v2/organizations — PaginatedResponse envelope, full org rows.
 * Used by the Organizations page (table + edit modal). Service flattens
 * `data` and requests limit=999.
 */
export const fetchOrganizations = async (): Promise<OrganizationListRow[]> => {
  const res = await instance.get<{ data: OrganizationListRow[] }>(
    `${ADMIN_PREFIX}/organizations`,
    { params: { page: 1, limit: 999 } }
  );

  return res.data.data ?? [];
};

/**
 * GET /api/v2/organizations/select — slim { id, name, image } projection
 * as a bare array. Used by the layout header org-picker so the dropdown
 * load doesn't pull settings / commission / additional_info bytes it
 * never renders.
 */
export const fetchOrganizationsForPicker = async (): Promise<
  Array<Pick<OrganizationListRow, 'id' | 'name' | 'image'>>
> => {
  const res = await instance.get<
    Array<Pick<OrganizationListRow, 'id' | 'name' | 'image'>>
  >(`${ADMIN_PREFIX}/organizations/select`);

  return res.data ?? [];
};

export const fetchOrganizationById = async (
  id: string | number
): Promise<OrganizationDetail> => {
  const res = await instance.get<OrganizationDetail>(
    `${ADMIN_PREFIX}/organizations/${id}`
  );

  return res.data;
};

export const createOrganization = (input: OrganizationInput) =>
  instance.post<OrganizationDetail>(`${ADMIN_PREFIX}/organizations`, input);

export const updateOrganization = (
  id: string | number,
  input: OrganizationInput
) =>
  instance.put<OrganizationDetail>(
    `${ADMIN_PREFIX}/organizations/${id}`,
    input
  );

/**
 * POST /api/v2/organizations/:id/admins — attach an admin to an
 * organization.
 *
 * Membership is NOT part of CreateUserDto: `organizations` on the create
 * body is rejected outright under forbidNonWhitelisted. An admin's
 * organizations are a separate call per organization, made after the
 * account exists.
 */
export const addAdminToOrganization = (
  organizationId: string | number,
  adminId: number
) =>
  instance.post<{ status: boolean }>(
    `${ADMIN_PREFIX}/organizations/${organizationId}/admins`,
    { adminId }
  );

/**
 * GET /api/v2/organizations/:id/admins/:adminId/detach-impact — what
 * removing this admin from this organization would strand.
 *
 * Read-only, and gated exactly like the detach it previews. `orphans` are
 * the admins this person created who are also members here: remove them
 * and those accounts stay in the organization with nobody able to see
 * them, because visibleAdminIds decides who appears in the admins list
 * from that same lineage. Ask this BEFORE offering the button, so the
 * operator can be made to choose a successor rather than told afterwards.
 */
export const fetchDetachImpact = async (
  organizationId: string | number,
  adminId: number
): Promise<{ orphans: number[]; requiresSuccessor: boolean }> => {
  const res = await instance.get<{
    orphans: number[];
    requiresSuccessor: boolean;
  }>(
    `${ADMIN_PREFIX}/organizations/${organizationId}/admins/${adminId}/detach-impact`
  );

  return res.data;
};

/**
 * DELETE /api/v2/organizations/:id/admins/:adminId — detach an admin,
 * handing their orphans to `reassignTo` in the same transaction.
 *
 * NOT A PLAIN DELETE. Without a successor the backend answers 400 naming
 * the count rather than stranding the accounts, and it refuses a successor
 * who is not in this organization or is the admin being detached. Both
 * halves commit together: reassigning afterwards from here would leave a
 * window where those accounts belong to nobody, made permanent by any
 * failure inside it.
 */
export const removeAdminFromOrganization = (
  organizationId: string | number,
  adminId: number,
  reassignTo?: number
) =>
  instance.delete<{ success: boolean; reassigned: number }>(
    `${ADMIN_PREFIX}/organizations/${organizationId}/admins/${adminId}`,
    reassignTo ? { params: { reassignTo } } : undefined
  );

export const deleteOrganization = (id: string | number) =>
  instance.delete<{ status: boolean }>(`${ADMIN_PREFIX}/organizations/${id}`);

/**
 * GET /api/v2/organizations/:id/children — slim sub-org picker
 * projection (id, name, image). Bare array. Mirrors /organizations/select
 * for the child level. Replaces the previous pattern of fetching the
 * heavy /organizations/:id detail just to read .children.
 */
export const fetchSubOrganizations = async (
  parentId: string | number
): Promise<Array<Pick<OrganizationListRow, 'id' | 'name' | 'image'>>> => {
  const res = await instance.get<
    Array<Pick<OrganizationListRow, 'id' | 'name' | 'image'>>
  >(`${ADMIN_PREFIX}/organizations/${parentId}/children`);

  return res.data ?? [];
};
